import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, Pencil, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PDFViewer from "../components/PDFViewer";

const API = axios.create({
  baseURL: "https://webapp-sitesync.onrender.com"
});

export default function Forms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState(null);
  const [expandedForm, setExpandedForm] = useState(null);
  // { formId, formName, submissionId? } -> submissionId presente = modo edição
  const [viewerTarget, setViewerTarget] = useState(null);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "supervisor") {
      navigate("/app/admin/forms");
      return;
    }

    const token = localStorage.getItem("ss_token");
    if (!token) {
      setError("Token not found");
      return;
    }

    API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    loadForms();
    loadSubmissions();
  }, [user, navigate]);

  const loadForms = async () => {
    try {
      setError(null);
      const response = await API.get("/api/forms");
      const formsData = Array.isArray(response.data) ? response.data : [];
      setForms(formsData);
    } catch (err) {
      console.error("Erro ao carregar formulários:", err);
      setError(err.response?.data?.detail || "Erro ao carregar formulários");
      setForms([]);
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await API.get("/api/forms/submissions/all");
      const subsData = Array.isArray(response.data) ? response.data : [];
      setSubmissions(subsData);
    } catch (err) {
      console.error("Erro ao carregar submissões:", err);
    }
  };

  // Primeira vez preenchendo (ainda não existe submissão para este form)
  const handleFillNew = (form) => {
    const formId = form.id || form._id;
    setViewerTarget({ formId, formName: form.name, submissionId: null });
  };

  // Reabrir/editar uma submissão já salva — isso é o que faltava antes:
  // sem isso, depois de "Submitted" não havia nenhum jeito de reabrir.
  const handleEditSubmission = (form, submission) => {
    const formId = form.id || form._id;
    setViewerTarget({ formId, formName: form.name, submissionId: submission.id });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight">Forms</h1>
          <p className="text-muted-foreground mt-1 text-sm">Download, fill out and submit your forms</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Forms List */}
        <div className="space-y-3">
          {forms && forms.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-lg">No forms available</p>
            </div>
          ) : (
            forms?.map((form) => {
              const formId = form.id || form._id;
              const submission = submissions.find((s) => s.form_id === formId);
              const isSubmitted = !!submission;

              return (
                <div key={formId} className="border border-border rounded-lg bg-card overflow-hidden">
                  {/* Card Header */}
                  <div
                    className="p-5 cursor-pointer hover:bg-accent transition flex justify-between items-center"
                    onClick={() => setExpandedForm(expandedForm === formId ? null : formId)}
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-base">{form.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(form.created_at).toLocaleDateString("en-US")}
                      </p>

                      {submission && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="inline-block px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs rounded-full font-medium">
                            Submitted
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(submission.submitted_at).toLocaleDateString("en-US")}
                            {submission.updated_at && submission.updated_at !== submission.submitted_at && (
                              <> · edited {new Date(submission.updated_at).toLocaleDateString("en-US")}</>
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="text-muted-foreground text-xs">
                      {expandedForm === formId ? "▼" : "▶"}
                    </div>
                  </div>

                  {/* Card Content (Expandable) */}
                  {expandedForm === formId && (
                    <div className="border-t border-border p-5 bg-accent/30">
                      {isSubmitted ? (
                        <button
                          onClick={() => handleEditSubmission(form, submission)}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm transition flex items-center justify-center gap-2 font-medium"
                        >
                          <Pencil size={16} /> Open & Edit Submission
                        </button>
                      ) : (
                        <button
                          onClick={() => handleFillNew(form)}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm transition flex items-center justify-center gap-2 font-medium"
                        >
                          <Eye size={16} /> Open & Fill Form
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {viewerTarget && (
        <PDFViewer
          formId={viewerTarget.formId}
          formName={viewerTarget.formName}
          submissionId={viewerTarget.submissionId}
          onClose={() => setViewerTarget(null)}
          onSave={() => {
            setViewerTarget(null);
            loadSubmissions();
            loadForms();
          }}
        />
      )}
    </div>
  );
}
